/* Shopify authenticates the customer when rendering review-eligibility.liquid.
   This is an entry gate; Judge.me owns submission and verification after handoff. */
if (!customElements.get('ps-review-entry')) {
  customElements.define('ps-review-entry', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.dialog = this.querySelector('dialog');
      this.status = this.querySelector('[data-review-status]');
      this.form = this.querySelector('form');
      this.signin = this.querySelector('[data-review-signin]');
      this.otherAccount = this.querySelector('[data-review-other-account]');
      this.retry = this.querySelector('[data-review-retry]');
      this.start = this.querySelector('[data-review-start]');
      this.start.addEventListener('click', () => this.open(), { signal });
      this.retry.addEventListener('click', () => this.open(), { signal });
      this.querySelector('[data-review-close]').addEventListener('click', () => this.dialog.close(), { signal });
      this.dialog.addEventListener('close', () => {
        this.request?.abort();
        // The queued close event must not steal focus from the newly opened form.
        if (!this.handingOff) this.start.focus({ preventScroll: true });
        this.handingOff = false;
      }, { signal });
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.continue();
      }, { signal });
      // Also intercept the native widget entry before Judge.me's delegated handler.
      this.closest('ps-reviews').addEventListener('click', (event) => {
        if (event.target.closest('.jm-action-buttons__button, .jdgm-write-rev-link')) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.open();
        }
      }, { capture: true, signal });
    }

    async loadPurchases() {
      this.request?.abort();
      this.request = new AbortController();
      const signal = AbortSignal.any([this.request.signal, this.controller.signal, AbortSignal.timeout(30000)]);
      const url = new URL(this.closest('ps-reviews').dataset.reviewsUrl, location.origin);
      url.searchParams.set('section_id', 'review-eligibility');
      // Never reuse customer data from a previous account/session or cached response.
      url.searchParams.set('_review_check', String(Date.now()));
      const products = new Map();
      let customerKey;
      let customer;
      let eligible = false;
      let page = 1;
      while (true) {
        url.searchParams.set('page', String(page));
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal });
        if (!response.ok) throw new Error('Purchase check failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const data = doc.querySelector('[data-review-eligibility]');
        if (!data) throw new Error('Purchase check unavailable');
        if (data.dataset.signedIn === 'false') return { signedIn: false };
        if (data.dataset.signedIn !== 'true' || !/^\d+$/.test(data.dataset.customerKey || '') ||
            Number(data.dataset.page) !== page || !['true', 'false'].includes(data.dataset.eligible)) {
          throw new Error('Invalid purchase response');
        }
        if (customerKey && customerKey !== data.dataset.customerKey) throw new Error('Account changed');
        customerKey = data.dataset.customerKey;
        const email = (data.dataset.customerEmail || '').trim();
        if (!email || (customer && customer.email !== email)) throw new Error('Account changed');
        customer = { id: customerKey, email, fullName: data.dataset.customerName || '' };
        eligible ||= data.dataset.eligible === 'true';
        for (const item of data.querySelectorAll('[data-purchase]')) {
          const { productId, title, image } = item.dataset;
          if (!/^\d+$/.test(productId || '') || !title) throw new Error('Invalid purchase');
          products.set(productId, { id: productId, title, image });
        }
        if (!data.dataset.next) break;
        const next = new URL(data.dataset.next, location.origin);
        if (next.origin !== location.origin || Number(next.searchParams.get('page')) !== page + 1) {
          throw new Error('Invalid purchase pagination');
        }
        page++;
      }
      return { signedIn: true, customerKey, customer, eligible, products };
    }

    async open() {
      if (!this.dialog.open) this.dialog.showModal();
      this.form.hidden = true;
      this.signin.hidden = true;
      this.otherAccount.hidden = true;
      this.retry.hidden = true;
      this.status.textContent = 'Checking your purchases…';
      try {
        const result = await this.loadPurchases();
        if (!this.dialog.open) return;
        if (!this.showEligibility(result)) return;
        this.customerKey = result.customerKey;
        const list = this.querySelector('[data-review-purchases]');
        list.replaceChildren();
        for (const product of result.products.values()) this.addChoice(list, product.id, product.title, '', product.image);
        this.form.hidden = false;
        this.status.textContent = '';
      } catch (error) {
        this.showError(error);
      }
    }

    showEligibility(result) {
      if (!result.signedIn) {
        this.status.textContent = '';
        this.signin.hidden = false;
        return false;
      }
      if (!result.eligible) {
        this.status.textContent = 'We couldn’t find a past purchase on this account. Reviews are available to Paris Saint customers. If you used another email at checkout, sign in with that account.';
        this.otherAccount.hidden = false;
        return false;
      }
      return true;
    }

    addChoice(list, value, title, description, imageUrl) {
      const label = document.createElement('label');
      label.className = 'ps-review-gate__purchase';
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.name = 'purchase';
      input.value = value;
      input.addEventListener('change', () => {
        if (input.checked) list.querySelectorAll('input').forEach((other) => {
          if (other !== input) other.checked = false;
        });
      });
      label.append(input);
      if (imageUrl) {
        const url = new URL(imageUrl, location.origin);
        if (url.protocol === 'https:' || url.origin === location.origin) {
          const image = document.createElement('img');
          image.src = url.href;
          image.alt = '';
          image.width = 56;
          image.height = 64;
          image.loading = 'lazy';
          label.append(image);
        }
      }
      const text = document.createElement('span');
      text.textContent = title;
      if (description) {
        const small = document.createElement('small');
        small.textContent = description;
        text.append(small);
      }
      label.append(text);
      list.append(label);
    }

    async continue() {
      const button = this.querySelector('[data-review-continue]');
      if (button.disabled) return;
      button.disabled = true;
      this.retry.hidden = true;
      const selections = new FormData(this.form).getAll('purchase');
      const id = selections[0] ?? '';
      this.status.textContent = 'Confirming your purchase history…';
      try {
        // Recheck on handoff: signing out/changing accounts in another tab invalidates the picker.
        const result = await this.loadPurchases();
        if (!this.dialog.open) return;
        if (!this.showEligibility(result)) { this.form.hidden = true; return; }
        if (result.customerKey !== this.customerKey || selections.length > 1 || typeof id !== 'string' || (id && !result.products.has(id))) {
          throw new Error('Purchase selection changed');
        }
        await this.openJudgeMe(id ? result.products.get(id) : null, result.customer);
      } catch (error) {
        this.showError(error);
      } finally {
        button.disabled = false;
      }
    }

    showError(error) {
      if (!this.dialog.open || error.name === 'AbortError') return;
      this.form.hidden = true;
      this.status.textContent = 'We couldn’t confirm your purchases or open the review form. Please try again.';
      this.retry.hidden = false;
    }

    async openJudgeMe(product, customer) {
      const jdgm = window.jdgm;
      // Use the same native modal as the Happy Customers widget, bypassing its
      // catalog picker. Never set isPreVerified or manufacture a verification token.
      if (!jdgm?._WriteReviewModal || !jdgm.$) throw new Error('Judge.me not ready');
      const modal = new jdgm._WriteReviewModal(jdgm.$);
      // Select Judge.me's native single-screen form only for this instance.
      // Rendering is synchronous; restore the shared setting before yielding.
      const renderTemplates = modal._renderModalTemplates;
      modal._renderModalTemplates = function (...args) {
        const previousTheme = window.jdgmSettings.review_form_theme;
        try {
          window.jdgmSettings.review_form_theme = 'compact';
          return renderTemplates.apply(this, args);
        } finally {
          window.jdgmSettings.review_form_theme = previousTheme;
        }
      };
      // The page's global Judge.me identity can belong to a previous session.
      // Bind this form to the same fresh Shopify identity as its purchase check.
      modal._getCustomerAccountData = () => customer;
      // Judge.me normally recreates a hidden form on close. Each gated handoff
      // gets a fresh instance, so dispose this instance instead of recreating it.
      modal._closeModal = () => {
        modal._teardownKeyboardAccessibility();
        modal._restoreFocusOnClose();
        modal.$modal.stop(true, true).fadeOut(400, () => modal.$modal.remove());
        if (this.modal === modal) this.modal = null;
      };
      if (product) {
        // This page has no product widget from which Judge.me can read metadata.
        modal._getProductDataFromDOM = () => ({ title: product.title, image_url: product.image || '' });
      }
      await modal.setup('jdgm-review-widget-modal', product ? product.id : 1, product ? {} : {
        isLeavingShopReview: true,
        startedStoreReviewFromIntro: true,
      });
      if (!this.dialog.open || !this.isConnected) {
        modal.$modal?.remove();
        return;
      }
      modal.$modal.addClass('ps-purchase-review-modal');
      modal.$anonymousCheckbox.prop('checked', false).trigger('change');
      modal.$anonymousCheckbox.closest('.jdgm-write-review-modal__field-group').hide();
      modal.$ytUrlInput.closest('.jdgm-write-review-modal__field-group').remove();
      modal.$ytUrlInput = jdgm.$();
      modal.$nameInput.closest('.jdgm-write-review-modal__field-group')
        .insertBefore(modal.$reviewBodyInput.closest('.jdgm-write-review-modal__field-group'));
      // Preserve the name field and its native handlers when switching review context.
      const remountDetails = modal._remountReviewDetailsPage;
      modal._remountReviewDetailsPage = function (...args) {
        const nameField = this.$nameInput.closest('.jdgm-write-review-modal__field-group').detach();
        const result = remountDetails.apply(this, args);
        nameField.insertBefore(this.$reviewBodyInput.closest('.jdgm-write-review-modal__field-group'));
        return result;
      };
      this.handingOff = true;
      this.dialog.close();
      this.modal = modal;
      modal.showModalPage(2);
      // The combined form owns rating entry; Back must not reopen the intro.
      modal.$navBackButton.hide();
      modal.$closeButton.show().css('opacity', 1).removeAttr('tabindex aria-hidden');
      // Keep one continuous full-screen surface rather than fading through the store.
      modal.$modal.stop(true, true).css('opacity', 1);
    }

    disconnectedCallback() {
      this.request?.abort();
      this.controller?.abort();
      this.dialog?.close();
      this.modal?._closeModal();
    }
  });
}
